import { useEffect, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import type { NoteTemplate } from 'app/generated/graphql'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { NOTE_TEMPLATE_LIST_PARAMS, type NoteTemplateInput } from 'app/stores/NoteTemplateStore'
import { useNoteTemplateStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

export type UseNoteTemplatesReturn = {
  templates: NoteTemplate[]
  saving: boolean
  setSearch: (value: string) => void
  editing: NoteTemplate | `new` | undefined
  openNew: () => void
  openEdit: (template: NoteTemplate) => void
  closeEditor: () => void
  saveTemplate: (input: NoteTemplateInput) => Promise<void>
  removeTemplate: (template: NoteTemplate) => Promise<void>
}

export function useNoteTemplates(): UseNoteTemplatesReturn {
  const store = useNoteTemplateStore()
  const [confirm] = useConfirmation()
  const [search, setSearch] = useState(``)
  const [editing, setEditing] = useState<NoteTemplate | `new` | undefined>()

  useEffect(() => {
    bg(store.fetchList(NOTE_TEMPLATE_LIST_PARAMS))
  }, [])

  const q = search.trim().toLowerCase()
  const templates = store.hits.filter(
    (t) => !q || (t.name ?? ``).toLowerCase().includes(q) || (t.body ?? ``).toLowerCase().includes(q),
  )

  const saveTemplate = async (input: NoteTemplateInput) => {
    if (!editing) return
    const result = editing === `new` ? await store.create(input) : await store.update(editing.id, input)
    if (result.error) {
      notifyError(`Couldn't save the note template`)
      logError(`useNoteTemplates: save failed`, result.error.message)
      return
    }
    notifySuccess(editing === `new` ? `Note template created` : `Note template updated`)
    setEditing(undefined)
  }

  const removeTemplate = async (template: NoteTemplate) => {
    const confirmed = await confirm({
      title: `Delete note template`,
      description: `Delete "${template.name}"? Notes already added are not affected.`,
      variant: `danger`,
    }).then(
      () => true,
      () => false,
    )
    if (!confirmed) return
    const result = await store.remove(template.id)
    if (result.error) {
      notifyError(`Couldn't delete the note template`)
      logError(`useNoteTemplates: delete failed`, result.error.message)
      return
    }
    notifySuccess(`Note template deleted`)
  }

  return {
    templates,
    saving: store.saving,
    setSearch,
    editing,
    openNew: () => setEditing(`new`),
    openEdit: setEditing,
    closeEditor: () => setEditing(undefined),
    saveTemplate,
    removeTemplate,
  }
}
